"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { ObjectRiskFocusDraft } from "@/lib/objectRiskFocus";
import { createFocusedObjectFile } from "@/lib/objectRiskFocus";
import { sanitizeSam3RoiPolygon } from "@/lib/sam3Geometry";
import type {
  Sam3Point,
  Sam3PointLabel,
  Sam3RoiPoint,
  Sam3SelectionMode,
  Sam3SelectionResponse,
} from "@/lib/sam3Contract";
import {
  createSam3InteractiveSession,
  deleteSam3InteractiveSession,
  Sam3ClientError,
  segmentSam3Points,
  segmentSam3Roi,
  segmentSam3Text,
} from "@/lib/sam3Client";

type Sam3Status = "idle" | "preparing" | "ready" | "segmenting" | "error";

type ImageState = {
  file: File;
  url: string;
  width: number;
  height: number;
};

const acceptedTypes = new Set(["image/png", "image/jpeg", "image/webp"]);
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

const loadImageSize = (url: string) =>
  new Promise<{ width: number; height: number }>((resolve, reject) => {
    const image = new Image();
    image.onload = () =>
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => reject(new Error("이미지를 열지 못했습니다."));
    image.src = url;
  });

export function useSam3Selection() {
  const [image, setImage] = useState<ImageState | null>(null);
  const [status, setStatus] = useState<Sam3Status>("idle");
  const [mode, setModeState] = useState<Sam3SelectionMode>("point");
  const [pointLabel, setPointLabel] = useState<Sam3PointLabel>(1);
  const [points, setPoints] = useState<Sam3Point[]>([]);
  const [lassoPolygon, setLassoPolygon] = useState<Sam3RoiPoint[]>([]);
  const [lassoResetKey, setLassoResetKey] = useState(0);
  const [textPrompt, setTextPrompt] = useState("");
  const [result, setResult] = useState<Sam3SelectionResponse | null>(null);
  const [activeInstanceIndex, setActiveInstanceIndex] = useState(0);
  const [embeddingSeconds, setEmbeddingSeconds] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [focusDraft, setFocusDraft] = useState<ObjectRiskFocusDraft | null>(null);
  const [focusPreparing, setFocusPreparing] = useState(false);

  const imageRef = useRef<ImageState | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const prepareAbortRef = useRef<AbortController | null>(null);
  const selectionAbortRef = useRef<AbortController | null>(null);
  const requestSequenceRef = useRef(0);

  const revokeFocusDraft = useCallback(() => {
    setFocusDraft((previous) => {
      if (previous) URL.revokeObjectURL(previous.previewUrl);
      return null;
    });
  }, []);

  const releaseSession = useCallback(async () => {
    const sessionId = sessionIdRef.current;
    sessionIdRef.current = null;
    if (!sessionId) return;
    try {
      await deleteSam3InteractiveSession(sessionId);
    } catch {
      // Server sessions expire automatically.
    }
  }, []);

  const prepareSession = useCallback(
    async (target: ImageState): Promise<string | null> => {
      prepareAbortRef.current?.abort();
      const controller = new AbortController();
      prepareAbortRef.current = controller;
      setStatus("preparing");
      setError(null);
      try {
        const session = await createSam3InteractiveSession(
          target.file,
          controller.signal,
        );
        if (imageRef.current?.url !== target.url) {
          void deleteSam3InteractiveSession(session.session_id).catch(() => undefined);
          return null;
        }
        sessionIdRef.current = session.session_id;
        setEmbeddingSeconds(session.embedding_seconds);
        setStatus("ready");
        return session.session_id;
      } catch (caught) {
        if (controller.signal.aborted) return null;
        setStatus("error");
        setError(
          caught instanceof Error
            ? caught.message
            : "SAM3 이미지 준비에 실패했습니다.",
        );
        return null;
      } finally {
        if (prepareAbortRef.current === controller) prepareAbortRef.current = null;
      }
    },
    [],
  );

  const resetSelection = useCallback(() => {
    selectionAbortRef.current?.abort();
    requestSequenceRef.current += 1;
    setPoints([]);
    setLassoPolygon([]);
    setLassoResetKey((value) => value + 1);
    setResult(null);
    setActiveInstanceIndex(0);
    revokeFocusDraft();
    setError(null);
    setStatus(sessionIdRef.current ? "ready" : imageRef.current ? "preparing" : "idle");
  }, [revokeFocusDraft]);

  const setMode = useCallback(
    (next: Sam3SelectionMode) => {
      if (next === mode) return;
      setModeState(next);
      resetSelection();
    },
    [mode, resetSelection],
  );

  const selectFile = useCallback(
    async (file: File) => {
      if (!acceptedTypes.has(file.type)) {
        setError("PNG, JPG 또는 WEBP 이미지를 선택해주세요.");
        return;
      }
      if (file.size <= 0 || file.size > MAX_IMAGE_BYTES) {
        setError("이미지는 10MB 이하여야 합니다.");
        return;
      }

      prepareAbortRef.current?.abort();
      selectionAbortRef.current?.abort();
      requestSequenceRef.current += 1;
      await releaseSession();

      const previous = imageRef.current;
      if (previous) URL.revokeObjectURL(previous.url);

      const url = URL.createObjectURL(file);
      try {
        const size = await loadImageSize(url);
        const next = { file, url, ...size };
        imageRef.current = next;
        setImage(next);
        setPoints([]);
        setLassoPolygon([]);
        setLassoResetKey((value) => value + 1);
        setResult(null);
        setActiveInstanceIndex(0);
        revokeFocusDraft();
        setEmbeddingSeconds(null);
        setError(null);
        await prepareSession(next);
      } catch (caught) {
        URL.revokeObjectURL(url);
        setStatus("error");
        setError(caught instanceof Error ? caught.message : "이미지를 열지 못했습니다.");
      }
    },
    [prepareSession, releaseSession, revokeFocusDraft],
  );

  const runWithSessionRetry = useCallback(
    async (
      execute: (sessionId: string, signal: AbortSignal) => Promise<Sam3SelectionResponse>,
      allowSessionRetry = true,
    ): Promise<Sam3SelectionResponse | null> => {
      const currentImage = imageRef.current;
      if (!currentImage) return null;
      let sessionId = sessionIdRef.current;
      if (!sessionId) {
        sessionId = await prepareSession(currentImage);
        if (!sessionId) return null;
      }

      selectionAbortRef.current?.abort();
      const controller = new AbortController();
      selectionAbortRef.current = controller;
      const sequence = ++requestSequenceRef.current;
      setStatus("segmenting");
      setError(null);
      try {
        const response = await execute(sessionId, controller.signal);
        if (sequence !== requestSequenceRef.current) return null;
        revokeFocusDraft();
        setResult(response);
        setActiveInstanceIndex(0);
        setStatus("ready");
        return response;
      } catch (caught) {
        if (controller.signal.aborted || sequence !== requestSequenceRef.current) {
          return null;
        }
        if (
          allowSessionRetry &&
          caught instanceof Sam3ClientError &&
          caught.status === 404
        ) {
          sessionIdRef.current = null;
          const recreated = await prepareSession(currentImage);
          if (recreated) return runWithSessionRetry(execute, false);
          return null;
        }
        setStatus("error");
        setError(
          caught instanceof Error ? caught.message : "객체를 분리하지 못했습니다.",
        );
        return null;
      } finally {
        if (selectionAbortRef.current === controller) {
          selectionAbortRef.current = null;
        }
      }
    },
    [prepareSession, revokeFocusDraft],
  );

  const runPoints = useCallback(
    async (nextPoints: Sam3Point[]) => {
      if (!nextPoints.some((point) => point.label === 1)) {
        setResult(null);
        return;
      }
      await runWithSessionRetry((sessionId, signal) =>
        segmentSam3Points(sessionId, nextPoints, signal),
      );
    },
    [runWithSessionRetry],
  );

  const addPoint = useCallback(
    (point: Sam3Point) => {
      const nextPoint =
        point.label === 0 && !points.some((item) => item.label === 1)
          ? { ...point, label: 1 as const }
          : point;
      const next = [...points, nextPoint].slice(-64);
      setPoints(next);
      void runPoints(next);
    },
    [points, runPoints],
  );

  const undoPoint = useCallback(() => {
    const next = points.slice(0, -1);
    setPoints(next);
    if (next.length === 0 || !next.some((point) => point.label === 1)) {
      selectionAbortRef.current?.abort();
      requestSequenceRef.current += 1;
      setResult(null);
      setStatus(sessionIdRef.current ? "ready" : "idle");
      return;
    }
    void runPoints(next);
  }, [points, runPoints]);

  const selectActiveInstance = useCallback(
    (index: number) => {
      revokeFocusDraft();
      setActiveInstanceIndex(index);
    },
    [revokeFocusDraft],
  );

  const completeLasso = useCallback(
    (polygon: Sam3RoiPoint[]) => {
      const currentImage = imageRef.current;
      if (!currentImage) return;
      const sanitized = sanitizeSam3RoiPolygon(
        polygon,
        currentImage.width,
        currentImage.height,
      );
      if (sanitized.length < 3) {
        setError("선택 영역이 너무 작습니다. 객체를 조금 더 넓게 둘러주세요.");
        return;
      }
      setLassoPolygon(sanitized);
      setPoints([]);
      void runWithSessionRetry((sessionId, signal) =>
        segmentSam3Roi(
          sessionId,
          sanitized,
          currentImage.width,
          currentImage.height,
          signal,
        ),
      );
    },
    [runWithSessionRetry],
  );


  const runText = useCallback(
    async (prompt: string) => {
      const currentImage = imageRef.current;
      const normalized = prompt.trim();
      if (!currentImage) return;
      if (!normalized) {
        setError("찾고 싶은 객체를 입력해주세요.");
        return;
      }
      if (normalized.length > 500) {
        setError("텍스트 검색어는 500자 이하여야 합니다.");
        return;
      }

      selectionAbortRef.current?.abort();
      const controller = new AbortController();
      selectionAbortRef.current = controller;
      const sequence = ++requestSequenceRef.current;
      setStatus("segmenting");
      setError(null);
      try {
        const response = await segmentSam3Text(
          currentImage.file,
          normalized,
          controller.signal,
        );
        if (sequence !== requestSequenceRef.current) return;
        setTextPrompt(normalized);
        setPoints([]);
        setLassoPolygon([]);
        revokeFocusDraft();
        setResult(response);
        setActiveInstanceIndex(0);
        setStatus("ready");
      } catch (caught) {
        if (controller.signal.aborted || sequence !== requestSequenceRef.current) return;
        setStatus("error");
        setError(
          caught instanceof Error ? caught.message : "텍스트 검색에 실패했습니다.",
        );
      } finally {
        if (selectionAbortRef.current === controller) {
          selectionAbortRef.current = null;
        }
      }
    },
    [revokeFocusDraft],
  );

  const confirmFocus = useCallback(
    async (label: string) => {
      const currentImage = imageRef.current;
      const instance = result?.instances?.[activeInstanceIndex];
      if (!currentImage || !instance) return null;
      setFocusPreparing(true);
      setError(null);
      try {
        const file = await createFocusedObjectFile({
          sourceImageUrl: currentImage.url,
          sourceName: currentImage.file.name,
          instance,
        });
        const draft: ObjectRiskFocusDraft = {
          file,
          previewUrl: URL.createObjectURL(file),
          label: label.trim() || textPrompt || "선택 객체",
          selectionMethod: mode,
          textPrompt: mode === "text" ? textPrompt || undefined : undefined,
          score: instance.score,
          boxXYXY: instance.box_xyxy,
          imageWidth: currentImage.width,
          imageHeight: currentImage.height,
        };
        setFocusDraft((previous) => {
          if (previous) URL.revokeObjectURL(previous.previewUrl);
          return draft;
        });
        return draft;
      } catch (caught) {
        setError(
          caught instanceof Error
            ? caught.message
            : "선택 객체 이미지를 만들지 못했습니다.",
        );
        return null;
      } finally {
        setFocusPreparing(false);
      }
    },
    [activeInstanceIndex, mode, result, textPrompt],
  );

  const clearSelection = useCallback(() => {
    resetSelection();
  }, [resetSelection]);

  useEffect(() => {
    const previewUrl = focusDraft?.previewUrl;
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [focusDraft?.previewUrl]);

  useEffect(
    () => () => {
      prepareAbortRef.current?.abort();
      selectionAbortRef.current?.abort();
      void releaseSession();
      if (imageRef.current) URL.revokeObjectURL(imageRef.current.url);
    },
    [releaseSession],
  );

  return {
    image,
    status,
    mode,
    setMode,
    pointLabel,
    setPointLabel,
    points,
    lassoPolygon,
    lassoResetKey,
    textPrompt,
    result,
    activeInstanceIndex,
    setActiveInstanceIndex: selectActiveInstance,
    activeInstance: result?.instances?.[activeInstanceIndex] ?? null,
    focusDraft,
    focusPreparing,
    embeddingSeconds,
    error,
    selectFile,
    addPoint,
    undoPoint,
    completeLasso,
    runText,
    confirmFocus,
    clearSelection,
  };
}
